import { useContext } from 'react';
import styled from '@emotion/styled';
import type { BrandInsightsPageContext, Segments } from '@trinity/chat';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/index.js';
import { SegmentationDashboardContext } from '@trinity/chat-components/containers/Segmentation.Dashboard/context.js';
import chatBackgroundImg from '@trinity/chat-components/static/images/chat-background.png';
import { BackLink } from '../presentation/Back';
import { DigitalTwinChat } from './DigitalTwinChat';
import { DigitalTwinLoader } from './DigitalTwinLoader';
import type { DigitalTwinSegment } from './DigitalTwinsGallery';
import { DigitalTwinsGallery } from './DigitalTwinsGallery';

type FetchedSegment = Segments.Segment;

export type HCPDigitalTwinsProps = {
  segments: DigitalTwinSegment[];
  selectedSegmentId: number | null;
  /** Resolved detail for `selectedSegmentId` once its fetch completes — undefined while loading. */
  selectedSegment: FetchedSegment | undefined;
  onSelectSegment: (segment: DigitalTwinSegment) => void;
  onBack: () => void;
  onViewSegmentProfile: (segment: DigitalTwinSegment) => void;
  /** Rendered inside the gallery, above the card grid. Each app supplies its own "back to
   * project" link. */
  backLink?: React.ReactNode;
  /** Chat context to seed the Digital Twin chat session with. Omit (default) for no scoping —
   * only apps/frontend's BrandInsights integration currently builds a real value. */
  chatPageContext?: BrandInsightsPageContext.Item;
};

/**
 * The "Chat with Digital Twins" gallery/chat screen — pick a segment's card to start a Digital
 * Twin chat session with it, or go back to the gallery. Ported from apps/frontend's
 * HCPDigitalTwins screen; each app supplies its own segment data-fetching and routing.
 */
export const HCPDigitalTwins = ({
  segments,
  selectedSegmentId,
  selectedSegment,
  onSelectSegment,
  onBack,
  onViewSegmentProfile,
  backLink,
  chatPageContext,
}: HCPDigitalTwinsProps) => {
  const themingContext = useContext(AppThemingContext);

  const inChatMode = selectedSegmentId !== null;
  const isReady = inChatMode && !!selectedSegment;

  return (
    <Root whitelabelEnabled={themingContext?.enabled} chatMode={inChatMode}>
      {inChatMode && (
        <BackBar>
          <BackLink
            to="#"
            onClick={(e) => {
              e.preventDefault();
              onBack();
            }}
          />
        </BackBar>
      )}

      {/* Gallery mode — no segment selected */}
      {!inChatMode && (
        <DigitalTwinChat
          ttsConfig={null}
          galleryContent={
            <DigitalTwinsGallery
              segments={segments}
              onChatWithSegment={onSelectSegment}
              onViewSegmentProfile={onViewSegmentProfile}
              backLink={backLink}
            />
          }
        />
      )}

      {/* Loading mode — segment selected but its detail fetch hasn't resolved yet */}
      {inChatMode && !isReady && (
        <DigitalTwinChat ttsConfig={null} galleryContent={<DigitalTwinLoader />} />
      )}

      {/* Chat mode — segment detail loaded, full Digital Twin chat */}
      {isReady && (
        <SegmentationDashboardContext.Provider
          value={{
            segment: selectedSegment,
            name: selectedSegment.name,
            metadata: selectedSegment.metadata ?? { title: selectedSegment.name },
          }}
        >
          <DigitalTwinChat
            avatarId={selectedSegment.ttsSettings?.avatarId ?? undefined}
            ttsConfig={selectedSegment.ttsSettings?.ttsConfig ?? null}
            chatPageContext={chatPageContext}
          />
        </SegmentationDashboardContext.Provider>
      )}
    </Root>
  );
};

const Root = styled('div', {
  shouldForwardProp: prop => prop !== 'whitelabelEnabled' && prop !== 'chatMode',
})<{ whitelabelEnabled?: boolean; chatMode: boolean }>(({ whitelabelEnabled, chatMode }) => ({
  // In gallery mode DigitalTwinChat fills the full viewport below the navbar (no chrome above
  // it). In chat mode the 44px BackBar sits above the chat, so it's subtracted from its height.
  '--dt-chrome-height': chatMode ? '44px' : '0px',
  display: 'flex',
  flexDirection: 'column',
  minHeight: 'calc(100vh - var(--navbar-height, 60px))',
  ...(whitelabelEnabled
    ? { backgroundColor: '#FAFAFA' }
    : {
      background: `url(${chatBackgroundImg}) no-repeat`,
      backgroundSize: 'cover',
    }),
}));

const BackBar = styled('div')({
  display: 'flex',
  alignItems: 'center',
  height: 44,
  padding: '0 16px',
  background: 'white',
  borderBottom: '1px solid #e8e8e8',
  flexShrink: 0,
});
